import React, { memo } from 'react';

import { IconType } from '../../types/IconType';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { StatusType } from '../../types/StatusType';
import { Theme } from '../../types/Theme';
import { Button } from '../Button/Button';
import { PresetKey } from './types';
import { usePreset } from './usePreset';

interface Props {
  /** Renders a system icon (should always be provided unless set by a preset.) */
  icon?: IconType;
  /** Tooltip is used as a semantic accessible label for the icon button (should always be provided unless set by a preset.) */
  tooltip?: string;
  /** If the button is disabled */
  disabled?: boolean;
  /** Unique id */
  id?: string;
  /** If the button is loading (will be disabled) */
  isLoading?: boolean;
  preset?: PresetKey;
  /** Size of the icon button */
  size?: SharedButtonProps['size'];
  /** Optional status */
  status?: StatusType;
  /** The accessible (non-display) button text for the icon.  Takes priority over tooltip */
  text?: string;
  /** Light / Dark theme */
  theme?: Theme;
  /** Click handler */
  onClick?: () => void;
  /** PRIVATE APIs */
  PRIVATE_variant?: 'action' | 'primary' | 'secondary' | 'tertiary' | 'subtle';
}

export const IconButton = memo((props: Props) => {
  const {
    disabled,
    id,
    isLoading,
    status,
    text,
    theme,
    PRIVATE_variant = 'tertiary',
    onClick,
    preset,
    icon: propIcon,
    size: propSize,
    tooltip: propTooltip,
    ...rest
  } = props;

  const presetResult = usePreset(preset as PresetKey);

  const icon = preset ? presetResult.icon : propIcon;
  const size = preset ? presetResult.size : propSize;
  const tooltip = preset ? presetResult.tooltip : propTooltip;

  return (
    <Button
      {...rest}
      disabled={disabled}
      icon={icon}
      id={id}
      isLoading={isLoading}
      mode="icon"
      size={size}
      status={status}
      text={text || tooltip}
      theme={theme}
      tooltip={tooltip}
      onClick={onClick}
      variant={PRIVATE_variant}
    />
  );
});
